<script setup lang="ts">
    import { ref } from 'vue';
    import { ROUTES } from '@/router';
    import { useCaseSettingsViewModel } from '@/views/case-settings/composables/useCaseSettingsViewModel';
    import CaseSettingsSavedNotification from '@/views/case-settings/CaseSettingsSavedNotification.vue';
    import UnassignSurgeon from '@/views/case-settings/UnassignSurgeon.vue';
    import CaseSettingsSurgeonAndSpecifications from '@/views/case-settings/CaseSettingsSurgeonAndSpecifications.vue';
    import CaseSettingsPatientInformation from '@/views/case-settings/CaseSettingsPatientInformation.vue';
    import CtUpload from '@/views/case-settings/upload/ct/CtUploadView.vue';
    import HipSpineUpload from '@/views/case-settings/upload/hip-spine/HipSpineUploadView.vue';
    import CaseSettingsTopNotifications from '@/views/case-settings/CaseSettingsTopNotifications.vue';
    import CaseToasties from '@/views/case-settings/case-toasties/CaseToasties.vue';
    import SurgicalGuideSettings from '@/views/case-settings/SurgicalGuideSettings.vue';
    import XrayUpload from '@/views/case-settings/upload/xray/XrayUploadView.vue';
    import caseFileIcon from '@/assets/icons/case-file.svg';
    import { CASE_SETTINGS_TARGETS } from '@/domain/analytics/analyticsTargets';
    import stethoscopeIcon from '@/assets/icons/stethoscope.svg';
    import uploadIcon from '@/assets/icons/upload.svg';

    const vm = useCaseSettingsViewModel();
    await vm.load();

    const uploadSection = ref<null | HTMLElement>(null);

    async function onSave() {
        const { rerouted } = await vm.save();
        if (rerouted) {
            // in theory nextTick should work, but it does not.
            setTimeout(() => {
                uploadSection.value?.scrollIntoView({ behavior: 'auto', block: 'start' });
            }, 0);
        }
    }
</script>

<template>
    <v-layout>
        <app-sidebar>
            <router-link :to="{ name: ROUTES.HOME }">
                <app-link icon="mdi-arrow-left" data-testid="back-to-cases-button">
                    Back to your Cases
                </app-link>
            </router-link>
        </app-sidebar>

        <app-main-view>
            <CaseSettingsTopNotifications />

            <CaseToasties />

            <app-form-title>
                {{ vm.pageTitle }}
            </app-form-title>

            <div class="case-settings-container">
                <app-main-card class="pa-8">
                    <div class="section-title-div">
                        <img :src="caseFileIcon" alt="Case Information Icon" width="19px" />
                        <app-form-section-title class="ma-0"
                            >Case Information</app-form-section-title
                        >
                    </div>

                    <v-container class="px-10 pt-0">
                        <app-form-group label="Case Name" input-id="case-name-input">
                            <app-text-input
                                v-model="vm.surgery.name"
                                data-test-id="case-name-input"
                                input-id="case-name-input"
                                :disabled="vm.isCaseReadonly"
                                :error-messages="
                                    vm.showFieldError('surgeryName')
                                        ? 'Please provide a case name'
                                        : ''
                                "
                            />
                        </app-form-group>

                        <app-form-group
                            label="Description"
                            optional
                            input-id="case-name-description"
                        >
                            <app-textarea
                                v-model="vm.surgery.description"
                                :disabled="vm.isCaseReadonly"
                                input-id="case-name-description"
                                data-testid="case-name-description-input"
                                maxlength="255"
                            >
                            </app-textarea>
                        </app-form-group>

                        <app-form-group
                            label="Date of Surgery"
                            :data-analytics-id="CASE_SETTINGS_TARGETS.surgeryDate"
                            optional
                            input-id="case-surgery-date"
                        >
                            <app-text-input
                                v-model="vm.surgery.date"
                                type="date"
                                :disabled="vm.isCaseReadonly"
                                input-id="case-surgery-date"
                                :error-messages="
                                    vm.showFieldError('surgeryDate')
                                        ? 'Please provide a value date'
                                        : ''
                                "
                            />
                        </app-form-group>

                        <app-form-group label="Procedure" input-id="case-surgery-procedure">
                            <app-text-input
                                v-model="vm.surgery.procedure"
                                variant="outlined"
                                density="compact"
                                disabled
                                data-test-id="case-surgery-procedure-input"
                                input-id="case-surgery-procedure"
                            />
                        </app-form-group>

                        <app-form-group
                            label="Implant Side"
                            :data-analytics-id="CASE_SETTINGS_TARGETS.implantSide"
                        >
                            <app-toggle
                                v-model="vm.surgery.side"
                                :disabled="!vm.isNewCase || vm.isCaseReadonly"
                                :error-messages="
                                    vm.showFieldError('surgerySide')
                                        ? 'Please provide patient implant side'
                                        : ''
                                "
                                :options="[
                                    { label: 'Left', value: 'left' },
                                    { label: 'Right', value: 'right' },
                                ]"
                                data-testid="side-selector"
                            />
                        </app-form-group>
                    </v-container>
                </app-main-card>

                <CaseSettingsPatientInformation />
                <app-main-card class="pa-8">
                    <div class="section-title-div">
                        <img :src="stethoscopeIcon" alt="Stethoscope Icon" width="24px" />
                        <app-form-section-title class="ma-0">
                            Surgeon and Surgical Specifications
                        </app-form-section-title>
                    </div>

                    <v-container class="px-10 pt-0">
                        <CaseSettingsSurgeonAndSpecifications />
                    </v-container>
                </app-main-card>

                <app-main-card v-if="vm.showSurgicalGuideCard" class="pa-8">
                    <SurgicalGuideSettings />
                </app-main-card>

                <div class="save-button-container pb-5">
                    <CaseSettingsSavedNotification />
                    <app-form-actions :data-analytics-id="CASE_SETTINGS_TARGETS.saveCaseButton">
                        <app-form-action-notification
                            v-if="vm.isDirty"
                            message="You have unsaved changes on this page"
                            data-test-id="form-has-changes-warning"
                        />
                        <app-button-primary
                            :disabled="vm.isCaseReadonly || !vm.canSave"
                            @click="onSave()"
                        >
                            Save Case Settings
                        </app-button-primary>
                    </app-form-actions>
                </div>
            </div>

            <div
                v-if="!vm.isNewCase && !vm.isCaseReadonly"
                ref="uploadSection"
                class="uploads-container"
            >
                <app-form-section-title class="mb-8">Upload Section</app-form-section-title>

                <app-main-card
                    class="pa-8"
                    data-testid="uploads"
                    :data-analytics-id="CASE_SETTINGS_TARGETS.uploadSection"
                >
                    <div>
                        <div class="section-title-div">
                            <img :src="uploadIcon" alt="Upload Icon" width="24px" />
                            <app-form-section-title class="ma-0 mb-2"
                                >Uploads
                            </app-form-section-title>
                        </div>
                        <app-form-subtitle v-if="vm.allow2d3dPlanningEnabled" class="px-10">
                            Toggle to <b>CT</b> to upload an Axial CT scan and create an AI
                            generated, patient specific 3D model. <br />Toggle to <b>2D/3D</b> to
                            upload three X-rays (standing AP, lateral and oblique) to generate a
                            model from radiographs. <br />*Note: 2D/3D is in limited beta and may be
                            less accurate than CT.
                        </app-form-subtitle>
                        <div class="px-10">
                            <app-toggle
                                v-if="vm.allow2d3dPlanningEnabled"
                                v-model="vm.selectedModality"
                                :options="vm.modalities"
                                :disabled="vm.isModalityLocked"
                            />
                            <v-tabs-window v-model="vm.selectedModality" class="pt-5">
                                <v-tabs-window-item value="ct-scan">
                                    <ct-upload />
                                </v-tabs-window-item>
                                <v-tabs-window-item value="xray">
                                    <xray-upload />
                                </v-tabs-window-item>
                            </v-tabs-window>
                        </div>
                    </div>
                    <unassign-surgeon v-if="vm.showUnassignSurgeon" />
                </app-main-card>

                <app-main-card v-if="vm.showHipSpineCard" class="pa-8" data-testid="hip-spine">
                    <div>
                        <div class="section-title-div">
                            <img :src="uploadIcon" alt="Upload Icon" width="24px" />
                            <app-form-section-title class="ma-0 mb-2"
                                >Hip Spine
                            </app-form-section-title>
                        </div>
                        <div class="px-10">
                            <app-form-subtitle>
                                Hip Spine offers additional planning support for cup angle analysis
                                using X-rays. Upload a standing sagittal radiograph (with a
                                supplemental lumbar view if needed). You can also upload a flexed
                                seated sagittal radiograph to include spinal stiffness
                                classification. Ensure each file matches the correct position shown
                                within each upload box illustration.
                            </app-form-subtitle>

                            <HipSpineUpload />
                        </div>
                    </div>
                    <unassign-surgeon v-if="vm.showUnassignSurgeon" />
                </app-main-card>
            </div>
        </app-main-view>
    </v-layout>
</template>

<style scoped lang="scss">
    :deep(.v-field.v-field--variant-outlined) {
        border-radius: 4px !important;
    }

    .case-settings-container {
        display: flex;
        flex-direction: column;
        max-width: 1020px;
    }

    .save-button-container {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
    }

    .section-title-div {
        display: flex;
        align-items: center;
        gap: 16px;
        //padding:12px;
    }

    @media screen and (max-width: 1300px) {
        .case-settings-container {
            margin-right: 50px;
        }

        .uploads-container {
            margin-right: 50px;
        }
    }
</style>
